import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Info, TrendingDown, TrendingUp, ArrowRight } from "lucide-react";
import { Slider } from "@/components/ui/slider";
import { Reveal } from "@/components/Reveal";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SHAPES = ["round", "oval", "emerald", "princess", "cushion", "pear", "radiant", "marquise"];
const COLORS = ["D", "E", "F", "G", "H", "I", "J", "K"];
const CLARITIES = ["FL", "IF", "VVS1", "VVS2", "VS1", "VS2", "SI1", "SI2"];
const CUTS = ["Ideal", "Excellent", "Very Good"];

const usd = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 });

const ChipRow = ({ label, options, value, onChange, testid }) => (
  <div>
    <p className="mb-3 text-[11px] uppercase tracking-[0.25em] text-[#717680]">{label}</p>
    <div className="flex flex-wrap gap-2" data-testid={testid}>
      {options.map((o) => (
        <button
          key={o}
          data-testid={`${testid}-${o.toLowerCase().replace(/\s+/g, "-")}`}
          data-active={value === o}
          onClick={() => onChange(o)}
          className="chip capitalize"
        >
          {o}
        </button>
      ))}
    </div>
  </div>
);

const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="glass-panel rounded-lg px-3 py-2 text-xs">
      <p className="text-[#717680]">{label}</p>
      <p className="font-semibold text-[#E8DCC0]">{usd.format(payload[0].value)}</p>
    </div>
  );
};

export const DiamondMarketExplorer = ({ onRequestQuote }) => {
  const [origin, setOrigin] = useState("natural");
  const [shape, setShape] = useState("round");
  const [carat, setCarat] = useState(1.5);
  const [color, setColor] = useState("G");
  const [clarity, setClarity] = useState("VS1");
  const [cut, setCut] = useState("Excellent");
  const [quote, setQuote] = useState(null);
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(true);

  const params = useMemo(
    () => ({ origin, shape, carat, color, clarity, cut }),
    [origin, shape, carat, color, clarity, cut]
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const [q, tr] = await Promise.all([
          axios.get(`${API}/market/quote`, { params }),
          axios.get(`${API}/market/trend`, { params }),
        ]);
        if (cancelled) return;
        setQuote(q.data);
        setPoints(tr.data.points);
      } catch (e) {
        if (!cancelled) toast.error("Market index temporarily unavailable — please retry.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 220);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [params]);

  return (
    <section id="explorer" data-testid="market-explorer" className="mx-auto max-w-7xl px-6 py-24 lg:px-12 lg:py-32">
      <Reveal>
        <p className="eyebrow mb-4">Diamond Market Explorer</p>
        <h2 className="font-display max-w-3xl text-3xl font-light leading-snug tracking-tight text-white sm:text-4xl lg:text-5xl">
          Read the market <span className="gold-text italic">before you call us</span>
        </h2>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-[#B8BCC2]">
          Configure a stone and see where the indicative wholesale index sits today — then ask our desk
          for a firm quote against live inventory.
        </p>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="glass-panel mt-14 grid gap-10 rounded-3xl p-8 lg:grid-cols-[1.1fr_1fr] lg:p-12">
          <div className="space-y-8">
            <div data-testid="explorer-origin-toggle" className="inline-flex rounded-full border border-white/15 p-1">
              {["natural", "lab"].map((o) => (
                <button
                  key={o}
                  data-testid={`explorer-origin-${o}`}
                  data-active={origin === o}
                  onClick={() => setOrigin(o)}
                  className="chip !border-0 capitalize data-[active=false]:bg-transparent"
                >
                  {o === "lab" ? "Lab-grown" : "Natural"}
                </button>
              ))}
            </div>

            <ChipRow label="Shape" options={SHAPES} value={shape} onChange={setShape} testid="explorer-shape" />

            <div>
              <div className="mb-3 flex items-baseline justify-between">
                <p className="text-[11px] uppercase tracking-[0.25em] text-[#717680]">Carat weight</p>
                <span data-testid="explorer-carat-value" className="font-display text-2xl gold-text">
                  {carat.toFixed(2)} ct
                </span>
              </div>
              <Slider
                data-testid="explorer-carat-slider"
                value={[carat]}
                onValueChange={([v]) => setCarat(v)}
                min={0.3}
                max={15}
                step={0.05}
              />
              <div className="mt-2 flex justify-between text-[10px] uppercase tracking-wider text-[#717680]">
                <span>0.30</span>
                <span>15.00+</span>
              </div>
            </div>

            <ChipRow label="Color" options={COLORS} value={color} onChange={setColor} testid="explorer-color" />
            <ChipRow label="Clarity" options={CLARITIES} value={clarity} onChange={setClarity} testid="explorer-clarity" />
            <ChipRow label="Cut" options={CUTS} value={cut} onChange={setCut} testid="explorer-cut" />
          </div>

          <div className="flex flex-col rounded-2xl border border-white/10 bg-[#0E0E10]/60 p-7 lg:p-9" data-testid="explorer-output">
            <div className="flex items-center justify-between">
              <p className="text-[11px] uppercase tracking-[0.25em] text-[#717680]">Indicative wholesale range</p>
              {quote && (
                <span
                  data-testid="explorer-trend-badge"
                  className={`flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${
                    quote.trend_dir === "up" ? "bg-[#34D399]/10 text-[#34D399]" : "bg-[#F87171]/10 text-[#F87171]"
                  }`}
                >
                  {quote.trend_dir === "up" ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                  {quote.trend_pct > 0 ? "+" : ""}
                  {quote.trend_pct}% / 12mo
                </span>
              )}
            </div>

            <div className={`mt-5 transition-opacity duration-300 ${loading ? "opacity-40" : "opacity-100"}`}>
              {quote ? (
                <>
                  <p data-testid="explorer-price-range" className="font-display text-4xl font-light text-white sm:text-5xl">
                    {usd.format(quote.price_low)} <span className="text-[#717680]">—</span>{" "}
                    <span className="gold-text">{usd.format(quote.price_high)}</span>
                  </p>
                  <p className="mt-2 text-sm text-[#B8BCC2]" data-testid="explorer-per-carat">
                    ≈ {usd.format(quote.per_carat)} per carat · {quote.carat.toFixed(2)}ct {quote.shape},{" "}
                    {quote.color}/{quote.clarity}, {quote.cut} cut · {quote.origin === "lab" ? "lab-grown" : "natural"}
                  </p>
                </>
              ) : (
                <p className="font-display text-4xl text-[#717680]">—</p>
              )}
            </div>

            <div className="mt-8 h-[190px]" data-testid="explorer-chart">
              {points.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 4, right: 0, bottom: 0, left: 0 }}>
                  <defs>
                    <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#C9A24B" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#C9A24B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="month" tick={{ fontSize: 9, fill: "#717680" }} tickLine={false} axisLine={false} interval={2} />
                  <YAxis hide domain={["dataMin - 200", "dataMax + 200"]} />
                  <Tooltip content={<ChartTooltip />} cursor={{ stroke: "#C9A24B", strokeOpacity: 0.3 }} />
                  <Area type="monotone" dataKey="value" stroke="#C9A24B" strokeWidth={2} fill="url(#goldFill)" />
                </AreaChart>
              </ResponsiveContainer>
              )}
            </div>

            <div className="mt-6 flex items-start gap-2 rounded-lg border border-[#C9A24B]/25 bg-[#C9A24B]/5 px-4 py-3" data-testid="explorer-disclaimer">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-[#C9A24B]" />
              <p className="text-xs leading-relaxed text-[#B8BCC2]">
                <span className="text-[#E8DCC0]">Indicative market data, dated {quote?.as_of ?? "—"} — not a firm quote.</span>{" "}
                Simulated reference index, refreshed daily.
              </p>
            </div>

            <button data-testid="explorer-quote-cta" onClick={onRequestQuote} className="gold-btn mt-7 w-full">
              Get an Exact Quote <ArrowRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </Reveal>
    </section>
  );
};
